import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import { Divider, Hidden } from '@material-ui/core';
import moment from 'moment';
import messages from 'aurora-translations';
import { ADGMPanelInputsGeneratorBody, ADGMButton } from 'aurora-components';
import { keyEmptyCheck } from 'aurora-utils/utils';
import { BUYER_TYPES, BUYER_TYPES_OPTIONS, BUYER_TYPE_KEY, FILTERS_KEYS } from '../../constants';
import styles from './styles';

const AddBuyerFilters = ({
  intl,
  classes,
  buyerType,
  filters,
  onBuyerTypeChangeHandler,
  onFilterChangeHandler,
  onFetchBuyerDetailsHandler,
}) => {
  const BUYER_FILTERS_SECTIONS = useMemo(() => {
    const buyerTypeFilter = {
      name: BUYER_TYPE_KEY,
      type: 'dropdown',
      label: intl.formatMessage({ ...messages.party_type }),
      placeholder: intl.formatMessage({ ...messages.party_type }),
      value: buyerType,
      options: BUYER_TYPES_OPTIONS,
      onChange: (_, data) => onBuyerTypeChangeHandler(data?.value ?? BUYER_TYPES.UAE_RESIDENT),
      required: true,
      hideClearSelectionBtn: true,
    };
    switch (buyerType) {
      case BUYER_TYPES.UAE_RESIDENT:
        return [
          {
            fields: [
              buyerTypeFilter,
              {
                type: 'text',
                label: intl.formatMessage({ ...messages.eid_moi_num }),
                placeholder: '000-0000-000000-0',
                value: filters?.[FILTERS_KEYS?.[buyerType]?.eid],
                startAdornment: keyEmptyCheck('eid_moi'),
                onChange: e => onFilterChangeHandler(FILTERS_KEYS?.[buyerType]?.eid, e?.target?.value),
                mask: [
                  /\d/,
                  /\d/,
                  /\d/,
                  '-',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                  '-',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                  '-',
                  /\d/,
                ],
                required: true,
              },
              {
                name: FILTERS_KEYS?.[buyerType]?.dateOfBirth,
                type: 'date',
                date: moment(filters?.[FILTERS_KEYS?.[buyerType]?.dateOfBirth]).isValid()
                  ? moment(filters?.[FILTERS_KEYS?.[buyerType]?.dateOfBirth]).format('YYYY-MM-DD')
                  : '',
                onChange: date =>
                  onFilterChangeHandler(FILTERS_KEYS?.[buyerType]?.dateOfBirth, moment(date).format('YYYY-MM-DD')),
                dateComponentProps: {
                  label: keyEmptyCheck('Date_of_birth'),
                  placeholder: `${intl.formatMessage({ ...messages.select })} ${intl.formatMessage({
                    ...messages.Date_of_birth,
                  })}`,
                  value: moment(filters?.[FILTERS_KEYS?.[buyerType]?.dateOfBirth]).isValid()
                    ? moment(filters?.[FILTERS_KEYS?.[buyerType]?.dateOfBirth]).format('DD/MM/YYYY')
                    : '',
                  autoComplete: 'off',
                  handleInputChange: date =>
                    date &&
                    onFilterChangeHandler(FILTERS_KEYS?.[buyerType]?.dateOfBirth, moment(date).format('YYYY-MM-DD')),
                  required: true,
                },
              },
            ],
          },
        ];
      case BUYER_TYPES.COMPANY:
        return [
          {
            fields: [
              buyerTypeFilter,
              {
                type: 'text',
                label: intl.formatMessage({ ...messages.Trade_license_number }),
                placeholder: `${intl.formatMessage({ ...messages.enter })} ${intl.formatMessage({
                  ...messages.Trade_license_number,
                })}`,
                value: filters?.[FILTERS_KEYS?.[buyerType].tradeLicense],
                onChange: e => onFilterChangeHandler(FILTERS_KEYS?.[buyerType]?.tradeLicense, e?.target?.value),
                required: true,
              },
            ],
          },
        ];
      case BUYER_TYPES.GOVERNMENT:
        return [
          {
            fields: [
              buyerTypeFilter,
              {
                type: 'text',
                label: intl.formatMessage({ ...messages.entity_Id_number }),
                placeholder: intl.formatMessage({
                  ...messages.entity_Id_number,
                }),
                value: filters?.[FILTERS_KEYS?.[buyerType].licenseNumber],
                onChange: e => onFilterChangeHandler(FILTERS_KEYS?.[buyerType]?.licenseNumber, e?.target?.value),
                required: true,
              },
              {
                type: 'text',
                label: intl.formatMessage({ ...messages.Name_Arabic }),
                placeholder: `${intl.formatMessage({ ...messages.enter })} ${intl.formatMessage({
                  ...messages.Name_Arabic,
                })}`,
                value: filters?.[FILTERS_KEYS?.[buyerType].nameAr],
                onChange: e => onFilterChangeHandler(FILTERS_KEYS?.[buyerType]?.nameAr, e?.target?.value),
              },
              {
                type: 'text',
                label: intl.formatMessage({ ...messages.Name_English }),
                placeholder: `${intl.formatMessage({ ...messages.enter })} ${intl.formatMessage({
                  ...messages.Name_English,
                })}`,
                value: filters?.[FILTERS_KEYS?.[buyerType].nameEn],
                onChange: e => onFilterChangeHandler(FILTERS_KEYS?.[buyerType]?.nameEn, e?.target?.value),
              },
            ],
          },
        ];
      default:
        return [buyerTypeFilter];
    }
  }, [buyerType, filters]);

  const disableFetchButton = useMemo(() => {
    if (!buyerType) return true;
    switch (buyerType) {
      case BUYER_TYPES.UAE_RESIDENT:
        return !filters?.[FILTERS_KEYS?.[buyerType]?.eid] || !filters?.[FILTERS_KEYS?.[buyerType]?.dateOfBirth];
      case BUYER_TYPES.COMPANY:
        return !filters?.[FILTERS_KEYS?.[buyerType]?.tradeLicense];
      case BUYER_TYPES.GOVERNMENT:
        return !Object.values(filters)?.some(filterVal => !!filterVal);
      default:
        return false;
    }
  }, [buyerType, filters]);

  return (
    <>
      <ADGMPanelInputsGeneratorBody isEditable sections={BUYER_FILTERS_SECTIONS} bodyStyle={classes.filtersContainer} />
      <Hidden xsDown>
        <Divider className={classes.divider} />
      </Hidden>
      <div className={classes.fetchBtnHolder}>
        <ADGMButton
          className={classes.fetchBtn}
          disabled={disableFetchButton}
          onClick={() => onFetchBuyerDetailsHandler(buyerType)}
        >
          {keyEmptyCheck('get_details')}
        </ADGMButton>
      </div>
    </>
  );
};

AddBuyerFilters.propTypes = {
  intl: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  buyerType: PropTypes.string.isRequired,
  filters: PropTypes.object.isRequired,
  onBuyerTypeChangeHandler: PropTypes.func.isRequired,
  onFilterChangeHandler: PropTypes.func.isRequired,
  onFetchBuyerDetailsHandler: PropTypes.func.isRequired,
};

export default withStyles(styles)(AddBuyerFilters);
